import { useEffect, useState } from "react";
import { Card } from "../../ui/Card";
import { useOutletId, resolveOutletParam, ALL_OUTLETS_SENTINEL } from "../../lib/outlet";

type Outlet = { outletId: string; outletName: string };
type Session = {
  ok: boolean;
  user?: { firstName?: string; lastName?: string };
  outlets?: Outlet[];
};

type ReportSummary = {
  totalSpendingCurrWeek?: number;
  totalSpendingLastWeek?: number;
  totalSpendingCurrMonth?: number;
  totalSpendingLastMonth?: number;
  ordersCurrWeek?: number;
  ordersLastWeek?: number;
  ordersCurrMonth?: number;
  ordersLastMonth?: number;
};

type SpendCat = { category: string; total: number };

type Range = "week" | "ytd" | "year" | "12w";

const fmtSg = (n?: number) =>
  typeof n === "number"
    ? n.toLocaleString("en-SG", { style: "currency", currency: "SGD", maximumFractionDigits: 2 })
    : "S$ —";

export function Dashboard() {
  const [sess, setSess] = useState<Session | null>(null);
  const selected = useOutletId(ALL_OUTLETS_SENTINEL);
  // outletIds is the comma-joined string sent to /reports/* when the user
  // picks "All outlets"; single outletId otherwise.
  const outletParam = resolveOutletParam(selected, sess?.outlets ?? []);

  useEffect(() => {
    void fetch("/api/auth/session")
      .then((r) => r.json())
      .then(setSess)
      .catch(() => {});
  }, []);

  const greeting = sess?.user?.firstName ? `Welcome back, ${sess.user.firstName}!` : "Welcome back!";
  const outletCount = sess?.outlets?.length ?? 0;

  return (
    <div className="space-y-7 motion-safe:animate-[dashIn_400ms_ease-out] transition">
      <style>{`@keyframes dashIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }`}</style>
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-medium tracking-tight text-ink">{greeting}</h1>
          <p className="text-ink-2 mt-1">
            {outletCount > 0
              ? `${outletCount} outlets connected. Aiko is ready when you are.`
              : "Sign in to see your outlets."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PrimaryButton>+ New order</PrimaryButton>
          <SecondaryButton>↑ Upload invoice</SecondaryButton>
        </div>
      </header>

      <KpiSummary outletId={outletParam} />

      <section>
        <SectionLabel>
          ✨ Insights{" "}
          <span className="ml-2 inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber/40 text-amber bg-amber-soft/60">
            BETA
          </span>
        </SectionLabel>
        <Card tone="neutral">
          <p className="text-ink-2 text-sm">
            Ask Aiko for an insight by tapping the green bubble. She reads this
            week against last week and surfaces what changed.
          </p>
          <p className="text-ink-mute text-xs mt-2">
            Insights are AI-generated. Aiko asks before she acts on them.
          </p>
        </Card>
      </section>

      <SpendingOverview outletId={outletParam} />
    </div>
  );
}

function KpiSummary({ outletId }: { outletId: string | null }) {
  const [data, setData] = useState<ReportSummary | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!outletId) return;
    setErr(false);
    void fetch(
      `/api/zeemart/report/services/reports/order/summary?outletId=${encodeURIComponent(outletId)}`,
      { headers: { outletId } },
    )
      .then((r) => r.json())
      .then((d) => {
        const body = (d?.data ?? d) as ReportSummary;
        setData(body);
      })
      .catch(() => setErr(true));
  }, [outletId]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <SectionLabel>Weekly orders</SectionLabel>
        <div className="grid grid-cols-2 gap-3">
          <CoinKpi
            tone="green"
            label="This week"
            hint={countHint(data?.ordersCurrWeek)}
            value={err ? "—" : fmtSg(data?.totalSpendingCurrWeek)}
          />
          <CoinKpi
            tone="teal"
            label="Last week"
            hint={countHint(data?.ordersLastWeek)}
            value={err ? "—" : fmtSg(data?.totalSpendingLastWeek)}
          />
        </div>
      </div>
      <div>
        <SectionLabel>Monthly orders</SectionLabel>
        <div className="grid grid-cols-2 gap-3">
          <CoinKpi
            tone="mint"
            label="This month"
            hint={countHint(data?.ordersCurrMonth)}
            value={err ? "—" : fmtSg(data?.totalSpendingCurrMonth)}
          />
          <CoinKpi
            tone="amber"
            label="Last month"
            hint={countHint(data?.ordersLastMonth)}
            value={err ? "—" : fmtSg(data?.totalSpendingLastMonth)}
          />
        </div>
      </div>
    </div>
  );
}

function countHint(n?: number) {
  return typeof n === "number" ? `(${n.toLocaleString()} orders)` : "";
}

function SpendingOverview({ outletId }: { outletId: string | null }) {
  const [range, setRange] = useState<Range>("12w");
  const [bars, setBars] = useState<SpendCat[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!outletId) return;
    const { startDate, endDate } = rangeBounds(range);
    setLoading(true);
    void fetch(
      `/api/zeemart/report/services/reports/spendings/category?outletId=${encodeURIComponent(outletId)}&startDate=${startDate}&endDate=${endDate}`,
      { headers: { outletId } },
    )
      .then((r) => r.json())
      .then((d) => {
        const raw = (d?.data ?? d) as Array<Record<string, unknown>>;
        const next: SpendCat[] = Array.isArray(raw)
          ? raw.map((row) => ({
              category: String(row.category ?? row.categoryName ?? "Other"),
              total: Number(
                row.totalSpendingOnCategoryAmount ??
                  row.totalamountCategory ??
                  row.total ??
                  row.totalSpending ??
                  row.amount ??
                  0,
              ),
            }))
              .filter((r) => r.total > 0)
              .sort((a, b) => b.total - a.total)
              .slice(0, 8)
          : [];
        setBars(next);
      })
      .catch(() => setBars([]))
      .finally(() => setLoading(false));
  }, [outletId, range]);

  const max = bars.reduce((m, b) => Math.max(m, b.total), 0) || 1;

  return (
    <section>
      <div className="flex items-end justify-between">
        <SectionLabel>Spending overview</SectionLabel>
        <a className="text-spinach text-sm font-medium hover:underline" href="/app/reports">
          View spending report →
        </a>
      </div>
      <Card>
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div>
            <div className="text-xs text-ink-mute uppercase tracking-wider">Range</div>
            <div className="text-ink font-medium">
              {range === "12w" ? "Past 12 weeks" : range === "year" ? "Last year" : range === "ytd" ? "Year to date" : "This week"}
            </div>
          </div>
          <div className="inline-flex rounded-md border border-line bg-canvas overflow-hidden text-sm">
            {(["ytd", "year", "12w"] as Range[]).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRange(r)}
                className={
                  "px-3 py-1.5 border-r last:border-r-0 border-line transition " +
                  (range === r
                    ? "bg-mint text-spinach font-medium"
                    : "text-ink-2 hover:bg-canvas-2")
                }
              >
                {r === "ytd" ? "YTD" : r === "year" ? "Last year" : "Past 12 weeks"}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <Skeleton rows={3} />
        ) : bars.length === 0 ? (
          <div className="text-center text-ink-mute text-sm py-10">
            No spend recorded for this range. Place a PO or upload an invoice and Aiko will start tracking.
          </div>
        ) : (
          <div className="space-y-2">
            {bars.map((b) => (
              <div key={b.category} className="grid grid-cols-[10rem_1fr_6rem] gap-3 items-center">
                <div className="text-sm text-ink-2 truncate">{b.category}</div>
                <div className="h-2.5 rounded-full bg-mint/40 overflow-hidden">
                  <div
                    className="h-full bg-nomni-green rounded-full"
                    style={{ width: `${Math.max(2, (b.total / max) * 100)}%` }}
                  />
                </div>
                <div className="text-sm text-ink text-right tabular-nums">{fmtSg(b.total)}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </section>
  );
}

function rangeBounds(r: Range): { startDate: number; endDate: number } {
  const end = Date.now();
  const day = 86400 * 1000;
  if (r === "week") return { startDate: end - 7 * day, endDate: end };
  if (r === "12w") return { startDate: end - 84 * day, endDate: end };
  if (r === "year") {
    const lastYear = new Date(end);
    lastYear.setFullYear(lastYear.getFullYear() - 1);
    return { startDate: lastYear.getTime(), endDate: end };
  }
  // ytd
  const ytd = new Date(end);
  ytd.setMonth(0); ytd.setDate(1); ytd.setHours(0,0,0,0);
  return { startDate: ytd.getTime(), endDate: end };
}

function Skeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-2 animate-pulse">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="grid grid-cols-[10rem_1fr_6rem] gap-3 items-center">
          <div className="h-3 rounded bg-line/60" />
          <div className="h-2.5 rounded-full bg-line/40" />
          <div className="h-3 rounded bg-line/60" />
        </div>
      ))}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-xs uppercase tracking-wider text-ink-mute font-medium mb-3 flex items-center">
      {children}
    </h2>
  );
}

type CoinTone = "green" | "teal" | "mint" | "amber";
const COIN_BG: Record<CoinTone, string> = {
  green: "bg-nomni-green text-canvas-2",
  teal:  "bg-spinach/30 text-spinach",
  mint:  "bg-mint text-spinach",
  amber: "bg-amber/30 text-amber",
};
function CoinKpi({ tone, label, value, hint }: { tone: CoinTone; label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-line bg-canvas-2 p-4 flex items-center gap-4 shadow-sm">
      <div aria-hidden className={`w-12 h-12 rounded-full ${COIN_BG[tone]} flex items-center justify-center text-base shrink-0`}>
        🧾
      </div>
      <div className="min-w-0">
        <div className="text-xs text-ink-mute">{label}{hint ? ` ${hint}` : ""}</div>
        <div className="text-2xl font-medium text-ink tabular-nums tracking-tight truncate">{value}</div>
      </div>
    </div>
  );
}

function PrimaryButton({ children }: { children: React.ReactNode }) {
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1.5 rounded-md bg-nomni-green text-canvas-2 px-4 py-2 text-sm font-medium hover:brightness-110 transition shadow-sm shadow-nomni-green/20"
    >
      {children}
    </button>
  );
}
function SecondaryButton({ children }: { children: React.ReactNode }) {
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1.5 rounded-md border border-nomni-green text-spinach px-4 py-2 text-sm font-medium hover:bg-mint transition"
    >
      {children}
    </button>
  );
}
